/**
 * CustomerEditPanel — edit the customer record.
 *
 * Taylor: "for this crm to be useful we need to be able to edit all the customer
 * fields. Like updated contacts, address and stuff like that" … "make every field
 * that make sense editable".
 *
 * Every field the API allows is here (PATCH /api/customers/[id], allowlisted in
 * customer-edit-input.ts). Nothing is offered that the server would refuse, and
 * nothing the server accepts is withheld — an edit form that silently omits a
 * writable field is indistinguishable from a broken one.
 *
 * WHAT IS DELIBERATELY *NOT* HERE, and why each is different from "we forgot":
 *   - Metrics (spend, travelers, PNR counts, health, NPS) are Domo-fed and
 *     overwritten every night. Offering them would be offering a lie.
 *   - customer_id / account #-as-identity are the join keys the whole platform
 *     uses; changing one repoints a row at a different customer.
 *   - Address beyond city/state, sector/subsector, employees, revenue: these are
 *     Domo analytics fields the CRM does not own a column for yet. They render on
 *     the profile from a live Domo overlay, so an "edit" would have nowhere to go.
 *   - Contacts, entities, partner agencies and contracts have their OWN editors
 *     already (customers/[id]/contacts etc.). Duplicating them here would mean
 *     two write paths for one record.
 *   - Global account, fee structure and partner-agency flag are CRM-owned and DO
 *     persist — but the Relationship panel on this same page already edits them
 *     via the audited PUT /api/customer-settings/[id]. Same rule as above: one
 *     editor per field. Two controls for one value on one screen disagree until
 *     reload, and the column would have two write paths with two audit shapes.
 *
 * THE OVERNIGHT-REVERT WARNING IS THE POINT. While Domo owns identity
 * (CRM_IDENTITY_OWNER='domo', the current and requested state) the 09:00 UTC sync
 * overwrites every identity field. So an edit here is real, visible, and gone by
 * morning. The server says which fields that applies to via
 * `identity_reverts_at_next_sync`; this form says it BEFORE you type, on the
 * fields it affects, and again in the success toast. A form that stayed quiet
 * about it would generate data-loss reports for behaviour we chose.
 */
import { type FormEvent, useMemo, useState } from 'react';
import { apiFetch, ApiError } from '@/features/crm/lib/api-fetch';

/** Exactly the fields PATCH /api/customers/[id] accepts. */
type EditableCustomer = {
  name: string | null;
  accountExecutive: string | null;
  csm: string | null;
  opsManager: string | null;
  industry: string | null;
  sizeBand: string | null;
  domain: string | null;
  city: string | null;
  state: string | null;
  statusName: string | null;
  keyContact: string | null;
  keyContactEmail: string | null;
  lifecycleStage: string | null;
};

type FieldSpec = {
  key: keyof EditableCustomer;
  label: string;
  kind: 'text' | 'email' | 'select';
  options?: string[];
  hint?: string;
};

/**
 * IDENTITY — seeded from Domo, and while Domo owns it these revert nightly.
 * Grouped separately from the CRM-only fields for exactly that reason: the two
 * groups behave differently and the user needs to see which is which.
 */
const IDENTITY_FIELDS: FieldSpec[] = [
  { key: 'name', label: 'Customer name', kind: 'text' },
  // NO 'Account #'. The API still accepts accountNumber, but the column holds a
  // comma-joined LIST of booking/GDS codes (111 customers have more than one,
  // the longest is 1,024 characters) — so a single-line text box could only
  // ever replace the whole set with one value. Nothing here can edit that
  // safely, and Domo overwrites it nightly regardless. See lib/account-codes.
  { key: 'domain', label: 'Domain', kind: 'text', hint: 'e.g. cbtravel.com' },
  {
    key: 'statusName',
    label: 'Status',
    kind: 'select',
    options: ['Active', 'Inactive', 'Prospect'],
  },
  { key: 'accountExecutive', label: 'Account Executive', kind: 'text' },
  { key: 'csm', label: 'Client Success Manager', kind: 'text' },
  { key: 'opsManager', label: 'Operations Manager', kind: 'text' },
  { key: 'industry', label: 'Industry', kind: 'text' },
  {
    key: 'sizeBand',
    label: 'Size band',
    kind: 'select',
    options: ['Micro', 'Small', 'Mid Market', 'Large Market', 'Enterprise'],
  },
  { key: 'city', label: 'City', kind: 'text' },
  { key: 'state', label: 'State', kind: 'text' },
  { key: 'keyContact', label: 'Key contact', kind: 'text' },
  { key: 'keyContactEmail', label: 'Key contact email', kind: 'email' },
];

/**
 * CRM-ONLY — never in Domo, never touched by the sync. These persist.
 *
 * Just the lifecycle stage. Global-account, fee structure and partner-agency are
 * CRM-owned too, but the Relationship panel on this same page already edits them
 * (via the audited PUT /api/customer-settings/[id]). Repeating them here would
 * put two controls for one field on one screen — they would disagree until
 * reload — and give the column two write paths. Same reason contacts, entities
 * and contracts are not duplicated here.
 */
const CRM_ONLY_FIELDS: FieldSpec[] = [
  {
    key: 'lifecycleStage',
    label: 'Lifecycle stage',
    kind: 'select',
    options: ['onboarding', 'ramp', 'mature', 'at_risk', 'renewal', 'churned'],
    hint: 'Drives playbook eligibility and QBR cadence.',
  },
];

type PatchResponse = {
  customer_id: string;
  changed: string[];
  identity_reverts_at_next_sync?: boolean;
  note?: string;
};

export function CustomerEditPanel({
  customerId,
  initial,
  onClose,
  onSaved,
}: {
  customerId: string;
  initial: Partial<EditableCustomer>;
  onClose: () => void;
  /** Called after a successful save so the profile can refetch. */
  onSaved: (changed: string[]) => void;
}) {
  const [values, setValues] = useState<Partial<EditableCustomer>>(initial);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<PatchResponse | null>(null);

  /**
   * Only what actually MOVED. Sending the whole form would mean every save
   * "changes" every field — the outbox would record edits nobody made, and the
   * change log becomes useless for deciding what to flip first.
   */
  const dirty = useMemo(() => {
    const out: Record<string, string | null> = {};
    for (const spec of [...IDENTITY_FIELDS, ...CRM_ONLY_FIELDS]) {
      const before = initial[spec.key] ?? null;
      const after = values[spec.key] ?? null;
      if (before !== after) {
        const text = (after ?? '').trim();
        // Send an explicit null to clear. The API also coerces '' → null, but
        // relying on that would make the intent depend on server behaviour —
        // and the outbox would record '' as the new value rather than null.
        out[spec.key] = text === '' ? null : text;
      }
    }
    return out;
  }, [initial, values]);

  const dirtyKeys = Object.keys(dirty);
  const identityKeys = new Set(IDENTITY_FIELDS.map((f) => f.key as string));
  const touchesIdentity = dirtyKeys.some((k) => identityKeys.has(k));

  const set = (key: keyof EditableCustomer, v: string | null) =>
    setValues((prev) => ({ ...prev, [key]: v }));

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (dirtyKeys.length === 0) return;
    setSaving(true);
    setError(null);
    setResult(null);
    try {
      const res = await apiFetch<PatchResponse>(
        `/api/customers/${encodeURIComponent(customerId)}`,
        { method: 'PATCH', body: JSON.stringify(dirty) },
      );
      setResult(res);
      onSaved(res.changed ?? dirtyKeys);
    } catch (err) {
      // Surface the server's message verbatim — it names the offending field(s),
      // which is the whole point of validating there rather than guessing here.
      setError(err instanceof ApiError ? err.message : String(err));
    } finally {
      setSaving(false);
    }
  };

  const renderField = (spec: FieldSpec) => {
    const raw = values[spec.key];
    const changed = spec.key in dirty;
    return (
      <label
        className="edit-field"
        data-changed={changed ? 'true' : undefined}
        key={spec.key}
      >
        <span className="edit-field-label">
          {spec.label}
          {changed ? <span className="edit-field-dot" aria-hidden /> : null}
        </span>

        {spec.kind === 'select' ? (
          <select
            data-testid={`edit-${spec.key}`}
            disabled={saving}
            onChange={(e) => set(spec.key, e.target.value || null)}
            value={raw ?? ''}
          >
            <option value="">— not set —</option>
            {spec.options?.map((o) => (
              <option key={o} value={o}>
                {o}
              </option>
            ))}
          </select>
        ) : (
          <input
            data-testid={`edit-${spec.key}`}
            disabled={saving}
            onChange={(e) => set(spec.key, e.target.value)}
            type={spec.kind === 'email' ? 'email' : 'text'}
            value={raw ?? ''}
          />
        )}

        {spec.hint ? (
          <span className="edit-field-hint">{spec.hint}</span>
        ) : null}
      </label>
    );
  };

  return (
    <div className="section" data-testid="customer-edit-panel">
      <div className="section-header">
        <span className="section-label">Edit customer</span>
        <button
          className="btn-ghost"
          data-testid="edit-cancel"
          onClick={onClose}
          type="button"
        >
          Close
        </button>
      </div>

      <div className="section-body">
        {/*
          Stated BEFORE the fields, not after saving. While Domo owns identity the
          09:00 UTC sync overwrites all of it, so this is not a caveat — it is the
          single most important thing to know before typing.
        */}
        <div className="banner banner-warn" data-testid="edit-revert-warning">
          <strong>Identity fields reset every night.</strong> Domo currently
          owns Customer name, Account #, Domain, Status, AE/CSM/Ops, Industry,
          Size and City/State — the 09:00 UTC sync overwrites them, so changes
          here are visible today and gone tomorrow. Useful for testing; not yet
          permanent. The lifecycle stage below is CRM-owned and does persist.
          Global account, fee structure and partner agency are CRM-owned too,
          but they are edited in the Relationship panel on this page.
        </div>

        {error ? (
          <div className="banner banner-error" data-testid="edit-error">
            {error}
          </div>
        ) : null}

        {result ? (
          result.changed.length === 0 ? (
            /*
             * The server wrote NOTHING because no value actually differed. This is
             * reachable even though the form only sends dirty fields: the form
             * diffs against what the profile displayed (which overlays live Domo
             * analytics), while the server diffs against the stored settings row.
             * Those can disagree. Reporting it as "Saved 0 fields" would be
             * technically true and useless — it reads like a failure. The server
             * sends a `note` explaining it; show that instead of inventing wording.
             */
            <div className="banner banner-info" data-testid="edit-noop">
              {result.note ??
                'Nothing was written — the values submitted already matched what is stored.'}
            </div>
          ) : (
            <div className="banner banner-success" data-testid="edit-success">
              Saved {result.changed.length} field
              {result.changed.length === 1 ? '' : 's'}:{' '}
              {result.changed.join(', ')}.
              {result.identity_reverts_at_next_sync
                ? ' Identity fields among these will revert at the next 09:00 UTC sync.'
                : ''}
            </div>
          )
        ) : null}

        <form onSubmit={onSubmit}>
          <fieldset className="edit-group" data-testid="edit-group-identity">
            <legend>
              Identity <span className="edit-group-note">(resets nightly)</span>
            </legend>
            <div className="edit-grid">{IDENTITY_FIELDS.map(renderField)}</div>
          </fieldset>

          <fieldset className="edit-group" data-testid="edit-group-crm">
            <legend>
              CRM-owned <span className="edit-group-note">(persists)</span>
            </legend>
            <div className="edit-grid">{CRM_ONLY_FIELDS.map(renderField)}</div>
          </fieldset>

          <div className="edit-actions">
            <button
              className="btn btn-primary"
              data-testid="edit-save"
              disabled={saving || dirtyKeys.length === 0}
              type="submit"
            >
              {saving
                ? 'Saving…'
                : dirtyKeys.length === 0
                  ? 'No changes'
                  : `Save ${dirtyKeys.length} change${dirtyKeys.length === 1 ? '' : 's'}`}
            </button>
            <button
              className="btn btn-outline"
              data-testid="edit-reset"
              disabled={saving || dirtyKeys.length === 0}
              onClick={() => {
                setValues(initial);
                setResult(null);
                setError(null);
              }}
              type="button"
            >
              Reset
            </button>
            {/* Say what will be sent. A save button that hides its payload is how
                you get "I only changed one thing" support threads. */}
            {dirtyKeys.length > 0 ? (
              <span className="edit-summary" data-testid="edit-dirty-summary">
                Sending: {dirtyKeys.join(', ')}
                {touchesIdentity ? ' — includes identity fields' : ''}
              </span>
            ) : null}
          </div>
        </form>
      </div>
    </div>
  );
}

export default CustomerEditPanel;
